import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listingsApi } from '../api/listings';
import { verfahrenApi } from '../api/verfahren';
import { anfragenApi } from '../api/anfragen';
import { aufgabeApi } from '../api/aufgabe';
import { useAuth } from '../context/AuthContext';
import { useIsModuleEnabled } from '../hooks/useConfigVisibility';

interface KpiCardProps {
  label: string;
  value: number | undefined;
  sublabel?: string;
  color?: 'default' | 'blue' | 'green' | 'yellow' | 'red' | 'orange';
  onClick?: () => void;
}

function KpiCard({ label, value, sublabel, color = 'default', onClick }: KpiCardProps) {
  const valueColors: Record<string, string> = {
    default: 'text-gray-900',
    blue:    'text-blue-600',
    green:   'text-green-600',
    yellow:  'text-yellow-600',
    red:     'text-red-600',
    orange:  'text-orange-600',
  };

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-lg shadow p-6 text-left w-full hover:shadow-md transition-shadow focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
    >
      <p className="text-sm font-medium text-gray-500 truncate">{label}</p>
      <p className={`mt-2 text-3xl font-bold ${valueColors[color]}`}>
        {value !== undefined ? value.toLocaleString('de-DE') : '–'}
      </p>
      {sublabel && (
        <p className="mt-1 text-xs text-gray-400">{sublabel}</p>
      )}
    </button>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // system_admin has no city and shouldn't fetch city-scoped data — send them
  // to the city overview instead. Done via useEffect so hooks below still run
  // unconditionally (Rules of Hooks).
  const isSystemAdmin = user?.role === 'system_admin';
  useEffect(() => {
    if (isSystemAdmin) navigate('/admin/cities', { replace: true });
  }, [isSystemAdmin, navigate]);

  // Only surface KPI groups whose page is enabled for this city.
  const showAufgaben = useIsModuleEnabled('aufgaben');
  const showInserate = useIsModuleEnabled('inserate');
  const showVerfahren = useIsModuleEnabled('verfahren');
  const showAnfragen = useIsModuleEnabled('anfragen');

  const { data: listingStats } = useQuery({
    queryKey: ['listings', 'stats'],
    queryFn: listingsApi.getStats,
    enabled: !isSystemAdmin,
  });

  const { data: verfahrenStats } = useQuery({
    queryKey: ['verfahren', 'stats'],
    queryFn: verfahrenApi.getStats,
    enabled: !isSystemAdmin,
  });

  const { data: anfragenData } = useQuery({
    queryKey: ['anfragen', 'total'],
    queryFn: () => anfragenApi.getAnfragen({ per_page: 1 }),
    enabled: !isSystemAdmin,
  });

  const { data: anfragenZulaessig } = useQuery({
    queryKey: ['anfragen', 'stats', 'zulaessig'],
    queryFn: () => anfragenApi.getAnfragen({ per_page: 1, status: 'zulaessig' }),
    enabled: !isSystemAdmin,
  });

  const { data: anfragenUnzulaessig } = useQuery({
    queryKey: ['anfragen', 'stats', 'unzulaessig'],
    queryFn: () => anfragenApi.getAnfragen({ per_page: 1, status: 'unzulaessig' }),
    enabled: !isSystemAdmin,
  });

  const { data: aufgabenCounts } = useQuery({
    queryKey: ['aufgaben', 'count'],
    queryFn: aufgabeApi.count,
    enabled: !isSystemAdmin,
  });
  const myAufgabenCount = aufgabenCounts?.offen;
  const myAufgabenOverdue = aufgabenCounts?.overdue;

  if (isSystemAdmin) return null;

  const offeneAnfragen =
    anfragenData !== undefined && anfragenZulaessig !== undefined && anfragenUnzulaessig !== undefined
      ? anfragenData.total - anfragenZulaessig.total - anfragenUnzulaessig.total
      : undefined;

  const offeneVerfahren =
    verfahrenStats !== undefined && verfahrenStats.by_state !== undefined
      ? verfahrenStats.total -
        (verfahrenStats.by_state['kein_verstoss'] ?? 0) -
        (verfahrenStats.by_state['verfahren_abgeschlossen'] ?? 0)
      : undefined;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* Meine Aufgaben */}
        {showAufgaben && <section>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Meine Aufgaben
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label="Mir zugewiesen"
              value={myAufgabenCount}
              color="blue"
              sublabel="Vorgänge, für die ich verantwortlich bin"
              onClick={() => navigate('/aufgaben')}
            />
            <KpiCard
              label="Wiedervorlage überfällig"
              value={myAufgabenOverdue}
              color={myAufgabenOverdue ? 'red' : 'green'}
              sublabel="Fällige Wiedervorlagen"
              onClick={() => navigate('/aufgaben?due=overdue')}
            />
          </div>
        </section>}

        {/* Inserate */}
        {showInserate && <section>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Inserate
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label="Inserate gesamt"
              value={listingStats?.total}
              sublabel="Alle erfassten Einträge"
              onClick={() => navigate('/inserate')}
            />
            <KpiCard
              label="Noch nicht gesichtet"
              value={listingStats?.by_state?.['neu']}
              color="blue"
              sublabel="Status: Neu"
              onClick={() => navigate('/inserate?state=neu')}
            />
            <KpiCard
              label="Aktuell online"
              value={listingStats?.is_online_count}
              color="green"
              sublabel="Derzeit aktive Angebote"
              onClick={() => navigate('/inserate?is_online=true')}
            />
            <KpiCard
              label="Verdachtsfälle"
              value={listingStats?.by_state?.['verdachtsfall']}
              color="yellow"
              sublabel="Mögliche Verstöße"
              onClick={() => navigate('/inserate?state=verdachtsfall')}
            />
          </div>
        </section>}

        {/* Verfahren */}
        {showVerfahren && <section>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Verfahren
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label="Verfahren gesamt"
              value={verfahrenStats?.total}
              sublabel="Alle Verfahren"
              onClick={() => navigate('/verfahren')}
            />
            <KpiCard
              label="Offene Verfahren"
              value={offeneVerfahren}
              color="orange"
              sublabel="Ohne Abschluss & kein Verstoß"
              onClick={() => navigate('/verfahren')}
            />
            <KpiCard
              label="Verstöße festgestellt"
              value={verfahrenStats?.by_state?.['verstoss_festgestellt']}
              color="red"
              sublabel="Bestätigte Verstöße"
              onClick={() => navigate('/verfahren')}
            />
            <KpiCard
              label="Bußgeld-Verfahren"
              value={verfahrenStats?.by_state?.['bussgeld']}
              color="red"
              sublabel="Verhängte Bußgelder"
              onClick={() => navigate('/verfahren')}
            />
          </div>
        </section>}

        {/* Anfragen */}
        {showAnfragen && <section>
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
            Anfragen
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard
              label="Anfragen gesamt"
              value={anfragenData?.total}
              sublabel="Alle eingegangenen Anfragen"
              onClick={() => navigate('/anfragen')}
            />
            <KpiCard
              label="Offene Anfragen"
              value={offeneAnfragen}
              color="orange"
              sublabel="Noch nicht abschließend bewertet"
              onClick={() => navigate('/anfragen?status=offene')}
            />
            <KpiCard
              label="Zulässige Anfragen"
              value={anfragenZulaessig?.total}
              color="green"
              sublabel="Als zulässig bewertet"
              onClick={() => navigate('/anfragen?status=zulaessig')}
            />
            <KpiCard
              label="Unzulässige Anfragen"
              value={anfragenUnzulaessig?.total}
              color="red"
              sublabel="Als unzulässig bewertet"
              onClick={() => navigate('/anfragen?status=unzulaessig')}
            />
          </div>
        </section>}

      </main>
    </div>
  );
}
